import React, { createContext, useContext, useState, useEffect } from 'react';
import profileService from '../services/ProfileService';
import { userDataService } from '../services/UserDataService';
import spotlightService from '../services/SpotlightService';

const ProfileContext = createContext();

// Maximum number of profiles allowed per account
const MAX_PROFILES = 5;

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [remainingProfiles, setRemainingProfiles] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedProfiles, setHasLoadedProfiles] = useState(false);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [isSwitchingProfiles, setIsSwitchingProfiles] = useState(false);
  const maxLoadAttempts = 3; // Maximum number of profile load attempts

  /**
   * Clear reactions from localStorage for a specific profile
   * Used when switching profiles or logging out
   */
  const clearReactionsFromLocalStorage = (profileId = null) => {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('reaction_')) {
          // If profileId is specified, only clear reactions for that profile
          if (profileId) {
            if (key.startsWith(`reaction_${profileId}_`)) {
              keysToRemove.push(key);
            }
          } else {
            // If no profileId specified, clear all reactions (legacy behavior)
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      
      const profileInfo = profileId ? ` for profile ${profileId}` : '';
      console.log(`ProfileContext: Cleared ${keysToRemove.length} reactions from localStorage${profileInfo}`);
    } catch (error) {
      console.warn('ProfileContext: Failed to clear reactions from localStorage:', error);
    }
  };

  // Initialize current profile from ProfileService on mount
  useEffect(() => {
    const loadSavedProfile = () => {
      const savedProfile = profileService.getCurrentProfile();
      if (savedProfile) {
        setCurrentProfile(savedProfile);
      } else {
        // If no profile found initially, retry after a short delay
        // This handles the case where ProfileService hasn't loaded from cache yet
        setTimeout(() => {
          const retryProfile = profileService.getCurrentProfile();
          if (retryProfile) {
            setCurrentProfile(retryProfile);
            // ProfileContext: Initialized with saved profile (retry)
          } else {
            // If still no profile found, try one more time after a longer delay
            setTimeout(() => {
              const finalRetryProfile = profileService.getCurrentProfile();
              if (finalRetryProfile) {
                setCurrentProfile(finalRetryProfile);
                // ProfileContext: Initialized with saved profile (final retry)
              } else {
              }
            }, 200);
          }
        }, 100);
      }
    };

    loadSavedProfile();
  }, []);

  // Fetch profile-specific data when current profile changes
  useEffect(() => {
    if (currentProfile?._id) {
      // ProfileContext: Profile selected, fetching profile-specific data

      // Fetch watchlist and resumption data for the selected profile
      const fetchProfileData = async () => {
        try {
          // Check if we have a user before fetching profile-specific data
          const user = spotlightService.getCurrentUser();
          if (!user || !user.email) {
            // ProfileContext: No authenticated user, skipping profile-specific data fetch
            return;
          }

          // Fetch watchlist data
          await userDataService.refreshWatchlistData();

          // Fetch resumption data
          await userDataService.refreshResumptionsData();
        } catch (error) {
          console.error('ProfileContext: Error fetching profile-specific data:', error);
        }
      };

      fetchProfileData();
    } else {
      // Clear profile-specific data when no profile is selected

      userDataService.clearCurrentUser();
    }
  }, [currentProfile?._id]);

  // Debug logging
  useEffect(() => {
    if (currentProfile) {
    } else {
    }
  }, [currentProfile]);

  /**
   * Reset load attempts (for manual retry)
   */
  const resetLoadAttempts = () => {
    setLoadAttempts(0);
    setError(null);
    setHasLoadedProfiles(false);
  };

  /**
   * Load all profiles for the current user
   */
  const loadProfiles = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setLoadAttempts(prev => prev + 1);

      const response = await profileService.getProfiles();

      if (response.success) {
        setProfiles(response.profiles || []);
        setRemainingProfiles(response.remaining || 0);
        setHasLoadedProfiles(true);
        setLoadAttempts(0); // Reset attempts on success
      } else {
        throw new Error('Failed to load profiles');
      }
    } catch (error) {
      console.error('ProfileContext: Error loading profiles:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create a new profile
   * @param {string} profileName - Name for the new profile
   * @returns {Promise<{success: boolean, error?: string}>} - Result with success status and optional error message
   */
  const createProfile = async profileName => {
    try {
      setIsLoading(true);

      const response = await profileService.createProfile(profileName);

      if (response.success && response.profile_created) {
        // Reload profiles from server to get the updated list
        await loadProfiles();
        return { success: true };
      } else {
        console.error('ProfileContext: Profile creation failed:', response);
        return { success: false, error: 'Failed to create profile' };
      }
    } catch (error) {
      console.error('ProfileContext: Error creating profile:', error);
      
      // Handle specific error cases and return them without setting global error
      if (error.message && error.message.includes('status: 409')) {
        // 409 Conflict - Profile limit reached
        return { success: false, error: 'Maximum number of profiles reached (5 profiles maximum)' };
      } else if (error.message && error.message.includes('status: 400')) {
        // 400 Bad Request - Invalid profile name or other validation error
        return { success: false, error: 'Invalid profile name. Please try a different name.' };
      } else {
        // Generic error
        return { success: false, error: error.message || 'Failed to create profile. Please try again.' };
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing profile's name
   * @param {string} profileId - Profile ID to update
   * @param {string} newName - New name for the profile
   * @returns {Promise<boolean>} - Whether the profile was updated successfully
   */
  const updateProfile = async (profileId, newName) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await profileService.updateProfile(profileId, newName);

      if (response.success && response.profile_updated) {
        // Reload profiles from server to get the updated list

        await loadProfiles();

        // Update current profile if it was the one being updated
        if (currentProfile && currentProfile._id === profileId) {
          const updatedProfiles = profileService.getCachedProfiles();
          const updatedCurrentProfile = updatedProfiles.find(p => p._id === profileId);
          if (updatedCurrentProfile) {
            setCurrentProfile(updatedCurrentProfile);
          }
        }

        return true;
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('ProfileContext: Error updating profile:', error);
      setError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a profile
   * @param {string} profileId - Profile ID to delete
   * @returns {Promise<boolean>} - Whether the profile was deleted successfully
   */
  const deleteProfile = async profileId => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await profileService.deleteProfile(profileId);

      if (response.success && response.profile_deleted) {
        // Reload profiles from server to get the updated list

        await loadProfiles();

        // Clear current profile if it was the one being deleted
        if (currentProfile && currentProfile._id === profileId) {
          setCurrentProfile(null);
        }

        return true;
      } else {
        throw new Error('Failed to delete profile');
      }
    } catch (error) {
      console.error('ProfileContext: Error deleting profile:', error);
      setError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Regenerate avatar for a profile
   * @param {string} profileId - Profile ID to regenerate avatar for
   * @returns {Promise<boolean>} - Whether the avatar was regenerated successfully
   */
  const regenerateAvatar = async profileId => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await profileService.regenerateAvatar(profileId);

      if (response.success) {
        // Reload profiles from server to get the updated avatar

        await loadProfiles();

        // Update current profile if it was the one being updated
        if (currentProfile && currentProfile._id === profileId) {
          const updatedProfiles = profileService.getCachedProfiles();
          const updatedCurrentProfile = updatedProfiles.find(p => p._id === profileId);
          if (updatedCurrentProfile) {
            setCurrentProfile(updatedCurrentProfile);
          }
        }

        return true;
      } else {
        throw new Error('Failed to regenerate avatar');
      }
    } catch (error) {
      console.error('ProfileContext: Error regenerating avatar:', error);
      setError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Set the active profile and get its token
   * @param {Object} profile - Profile to set as active
   * @returns {Promise<boolean>} - Whether the profile was set successfully
   */
  const setActiveProfile = async profile => {
    try {
      setIsLoading(true);
      setError(null);

      const success = await profileService.setActiveProfile(profile);

      if (success) {
        setCurrentProfile(profile);
        setIsSwitchingProfiles(false); // Clear the switching flag when a profile is selected

        // Sync user reactions from API to localStorage after profile is set
        try {
          await spotlightService.syncUserReactionsToLocalStorage();
        } catch (syncError) {
          console.warn('ProfileContext: Failed to sync reactions, continuing without them:', syncError);
        }

        return true;
      } else {
        throw new Error('Failed to set active profile');
      }
    } catch (error) {
      console.error('ProfileContext: Error setting active profile:', error);
      setError(error.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clear current profile (for logout or profile switching)
   */
  const clearCurrentProfile = () => {
    setIsSwitchingProfiles(true);
    setCurrentProfile(null);
    profileService.clearCurrentProfile();
    
    // Clear reactions from localStorage when switching profiles
    clearReactionsFromLocalStorage();
  };

  /**
   * Clear all profile data (for logout)
   */
  const clearAllData = () => {
    setProfiles([]);
    setCurrentProfile(null);
    setRemainingProfiles(0);
    setHasLoadedProfiles(false);
    setError(null);
    profileService.clearAllData();
    
    // Clear reactions from localStorage when logging out
    clearReactionsFromLocalStorage();
  };

  /**
   * Check if user needs to select a profile
   * @returns {boolean} - Whether profile selection is needed
   */
  const needsProfileSelection = () => {
    const result = hasLoadedProfiles && profiles.length > 0 && !currentProfile;
    return result;
  };

  /**
   * Check if user can create a new profile
   * Enforces both API remaining count and frontend maximum limit
   * @returns {boolean} - Whether a new profile can be created
   */
  const canCreateProfile = () => {
    return remainingProfiles > 0 && profiles.length < MAX_PROFILES;
  };

  /**
   * Get profile by ID
   * @param {string} profileId - Profile ID to find
   * @returns {Object|null} - Profile object or null if not found
   */
  const getProfileById = profileId => {
    return profiles.find(p => p._id === profileId) || null;
  };

  const value = {
    // State
    profiles,
    currentProfile,
    remainingProfiles,
    isLoading,
    error,
    hasLoadedProfiles,
    isSwitchingProfiles,

    // Actions
    loadProfiles,
    resetLoadAttempts,
    createProfile,
    updateProfile,
    deleteProfile,
    regenerateAvatar,
    setActiveProfile,
    clearCurrentProfile,
    clearAllData,

    // Helpers
    needsProfileSelection,
    canCreateProfile,
    getProfileById,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};
